import type { components } from '@/api'
import { listCases } from '@/backend/case'
import type { ListCases } from '@/backend/caseTypes'
import type { PaginationMetadata } from '@/backend/types'
import type { WorkspaceTopic } from '@/data/websocketTopics'
import { serializePaginatedCase } from '@/modules/Cases/serializers'
import type { PaginatedCase } from '@/modules/Cases/types'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { useWebSocketTopic } from '@/sharedComposables/useWebSocketTopic'
import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

export type UsePaginatedCasesParams = {
  search?: string | undefined
  order?: OrderTuple[]
}

/**
 * Provides a Tanstack query hook for loading paginated cases, with support for
 * searching.
 */
export const usePaginatedCases = (
  params: MaybeRefOrGetter<UsePaginatedCasesParams> = {},
  { disableLoading }: { disableLoading?: MaybeRefOrGetter<boolean> } = {},
) => {
  const { workspaceId } = useWorkspaceContext()

  const queryKey = computed(() => ['cases', toValue(params), workspaceId.value])

  const query = useInfiniteQuery<
    ListCasesResponse, // TQueryData - raw data from queryFn
    Error, // TError - error type
    PaginatedCase[], // TData - final transformed data (what consumers get)
    readonly (string | UsePaginatedCasesParams | undefined)[], // TQueryKey - query key type
    PaginationMetadata | null // TPageParam - page parameter type
  >({
    queryKey,
    enabled: () => !toValue(disableLoading),
    /**
     * Set quite a short garbage collection time because these infinite
     * queries could use a lot of memory.
     */
    gcTime: 10_000,
    queryFn: async ({ pageParam }) => {
      const { search, order } = toValue(params)
      const res = await listCases({
        workspaceId: workspaceId.value,
        after: pageParam?.end_cursor,
        first: 20,
        order,
        name_contains: search || undefined, // coalesce empty string to undefined
      })

      if (!res.ok) {
        throw new Error('Failed to load cases')
      }

      return res.data
    },
    getNextPageParam: (data) => {
      if (!data.metadata.has_next_page) {
        return null // returning null here indicates that there are no more pages
      }

      return data.metadata
    },
    /**
     * placeholderData is used so that when the search changes, the old
     * data is still visible and we don't get a flash of loading state.
     */
    placeholderData: (data) => data,
    initialPageParam: null as null | PaginationMetadata,
    /**
     * Flatten the pages of Cases when retuning the data to consumers.
     */
    select: (data) => data.pages.flatMap((page) => page.data.map(serializePaginatedCase)),
  })

  const queryClient = useQueryClient()
  /** Invalidate all paginated case queries, marking them all as stale. */
  const invalidateAll = useDebounceFn(
    () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] })
    },
    500, // debounce to 500ms
    { maxWait: 500 }, // max wait of 500ms
  )

  const workspaceTopic = computed<WorkspaceTopic>(() => `workspace:${workspaceId.value}`)
  const { subscribe } = useWebSocketTopic(workspaceTopic)
  /**
   * Whenever workspace case data changes, invalidate all paginated case queries.
   * We could be clever and directly update the query cache, but this gets very
   * complicated when we consider sorting, filtering, etc. It's much simpler to
   * mark as stale and refetch the data when it's needed.
   */
  subscribe('case:created', invalidateAll)
  subscribe('case:updated', invalidateAll)
  subscribe('case:deleted', invalidateAll)

  return {
    ...query,
    invalidateAll,
  }
}

type ListCasesResponse = ListCases['successResponse']

type OrderField = components['schemas']['Cases.ListCasesOrderBy'][number]
type OrderDirection = 'asc' | 'desc'
type OrderTuple = [OrderField, OrderDirection]
